import React, { useRef, useState } from "react";
import { ShieldAlert, Camera, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatZAR, formatApiErrorDetail } from "../lib/api";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;

/** Card on the booking page: shows an existing claim (with guest response form) or lets the host open one. */
export default function DamageClaimCard({ booking, user, onChanged }) {
  const claim = booking.claim;
  const isOwner = user?.id === booking.owner_id;
  const isGuest = user?.id === booking.customer_id;
  const completedAt = booking.completed_at || booking.returned_at;
  const withinWindow = completedAt && Date.now() - new Date(completedAt).getTime() < 72 * 3600 * 1000;
  const canOpen = isOwner && booking.status === "completed" && !claim && withinWindow && booking.deposit_refund_status !== "processed";

  if (!claim && !canOpen) return null;

  return (
    <section className="rounded-2xl border border-[#E2E8F0] p-5 bg-white" data-testid="damage-claim-card">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-[#FFFBEB] text-[#B45309] flex items-center justify-center flex-shrink-0"><ShieldAlert size={18} /></span>
        <div className="flex-1 min-w-0">
          <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Damage claim</p>
          {!claim && canOpen && <OpenClaim booking={booking} onChanged={onChanged} />}
          {claim && <ClaimView claim={claim} booking={booking} isGuest={isGuest} isOwner={isOwner} onChanged={onChanged} />}
        </div>
      </div>
    </section>
  );
}

function StatusPill({ status }) {
  const map = {
    open: ["bg-[#F59E0B]/15 text-[#B45309]", "Awaiting guest response"],
    under_review: ["bg-[#1d7ffd]/10 text-[#1d7ffd]", "Under review by tadada"],
    approved: ["bg-[#10B981]/15 text-[#065F46]", "Approved"],
    rejected: ["bg-[#EF4444]/15 text-[#B91C1C]", "Rejected"],
  };
  const [cls, label] = map[status] || ["bg-[#F0F2F5] text-[#0a1f4d]", status];
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${cls}`} data-testid="claim-status">{label}</span>;
}

function PhotoStrip({ photos, testid }) {
  if (!photos?.length) return null;
  return (
    <div className="flex gap-2 flex-wrap mt-2" data-testid={testid}>
      {photos.map((p, i) => (
        <a key={p.file_id + i} href={`${API_ORIGIN}${p.url}`} target="_blank" rel="noreferrer" className="block">
          <img src={`${API_ORIGIN}${p.url}`} alt="" className="w-16 h-16 object-cover rounded-lg border border-[#E2E8F0]" />
          <p className="text-[9px] text-[#64748B] text-center capitalize">{p.phase}{p.by_role ? ` · ${p.by_role}` : ""}</p>
        </a>
      ))}
    </div>
  );
}

function ClaimView({ claim, booking, isGuest, isOwner, onChanged }) {
  return (
    <div className="mt-1">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-display font-bold text-[#0a1f4d]">{formatZAR(claim.amount_claimed)} claimed by {isOwner ? "you" : claim.owner_name}</p>
        <StatusPill status={claim.status} />
      </div>
      <p className="text-xs text-[#64748B] mt-0.5">Opened {format(new Date(claim.created_at), "d MMM, HH:mm")} · deposit {formatZAR(claim.deposit)} {claim.decision ? "was held during review" : "on hold"}</p>
      <p className="text-sm text-[#334155] mt-3 whitespace-pre-line" data-testid="claim-description">{claim.description}</p>
      <PhotoStrip photos={claim.photos} testid="claim-photos" />

      {claim.guest_response ? (
        <div className="mt-4 rounded-xl bg-[#F7F8FA] border border-[#E2E8F0] p-3" data-testid="claim-guest-response">
          <p className="text-[11px] font-bold text-[#0a1f4d] uppercase tracking-wide">Guest response · {format(new Date(claim.guest_response.created_at), "d MMM, HH:mm")}</p>
          <p className="text-sm text-[#334155] mt-1 whitespace-pre-line">{claim.guest_response.text}</p>
          <PhotoStrip photos={claim.guest_response.photos} testid="claim-guest-photos" />
        </div>
      ) : (
        isGuest && ["open", "under_review"].includes(claim.status) && <GuestResponse booking={booking} claim={claim} onChanged={onChanged} />
      )}

      {claim.decision && (
        <div className={`mt-4 rounded-xl p-3 text-sm ${claim.status === "approved" ? "bg-[#FFFBEB] text-[#78350F]" : "bg-[#10B981]/10 text-[#065F46]"}`} data-testid="claim-decision">
          <p className="font-bold inline-flex items-center gap-1.5">{claim.status === "approved" ? <CheckCircle2 size={14} /> : <XCircle size={14} />} tadada decision · {format(new Date(claim.decision.decided_at), "d MMM, HH:mm")}</p>
          <p className="mt-1">
            {claim.status === "approved"
              ? `${formatZAR(claim.decision.amount_approved)} approved and deducted from the deposit. Guest refund: ${formatZAR(claim.decision.guest_refund)}. ${isOwner ? "It will be added to your next Friday payout." : ""}`
              : `Claim rejected. Full deposit refund of ${formatZAR(claim.decision.guest_refund)} is being processed.`}
          </p>
          {claim.decision.note && <p className="mt-1 text-xs opacity-80">“{claim.decision.note}”</p>}
        </div>
      )}
      {!claim.decision && (
        <p className="mt-3 text-xs text-[#64748B] inline-flex items-center gap-1"><Clock size={12} /> {claim.status === "open" ? `Guest can respond until ${format(new Date(claim.respond_by), "d MMM, HH:mm")}; tadada then decides.` : "tadada is reviewing both sides and will decide shortly."}</p>
      )}
    </div>
  );
}

export function usePhotoUploads() {
  const [files, setFiles] = useState([]); // [{id,url}]
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const add = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Images only");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((f) => [...f, data]);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return { files, busy, ref, add, reset: () => setFiles([]) };
}

const UploadRow = ({ up, label, testid }) => (
  <div className="flex items-center gap-2 flex-wrap">
    {up.files.map((f) => <img key={f.id} src={`${API_ORIGIN}${f.url}`} alt="" className="w-12 h-12 rounded-lg object-cover border border-[#E2E8F0]" />)}
    <input ref={up.ref} type="file" accept="image/*" className="hidden" onChange={(e) => up.add(e.target.files?.[0])} data-testid={`${testid}-file`} />
    <button type="button" onClick={() => up.ref.current?.click()} disabled={up.busy} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0a1f4d] border border-[#E2E8F0] rounded-full px-3 py-1.5 hover:border-[#0a1f4d] disabled:opacity-60" data-testid={`${testid}-add`}>
      {up.busy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />} {label}
    </button>
  </div>
);

export function ClaimForm({ booking, value, onChange, compact = false }) {
  const returnPhotos = (booking.handover_photos || []).filter((p) => p.phase === "return");
  const pickupPhotos = (booking.handover_photos || []).filter((p) => p.phase === "pickup");
  const toggle = (id) => onChange({ ...value, photo_ids: value.photo_ids.includes(id) ? value.photo_ids.filter((x) => x !== id) : [...value.photo_ids, id] });
  const maxClaim = Math.max(0, (booking.pricing?.deposit || 0) - (booking.late_fee || 0) - (booking.excess_km_fee || 0));
  return (
    <div className={`space-y-3 ${compact ? "" : "mt-3"}`} data-testid="claim-form">
      <label className="block">
        <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Amount claimed (max {formatZAR(maxClaim)} = deposit{booking.late_fee ? " − late fee" : ""})</span>
        <input type="number" min="1" step="1" className="ipc mt-1" value={value.amount_claimed} onChange={(e) => onChange({ ...value, amount_claimed: e.target.value })} placeholder="e.g. 850" data-testid="claim-amount" />
      </label>
      <label className="block">
        <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">What happened?</span>
        <textarea className="ipc mt-1 min-h-[80px]" value={value.description} onChange={(e) => onChange({ ...value, description: e.target.value })} placeholder="Describe the damage, where it is, and how it differs from the pickup photos…" data-testid="claim-description-input" />
      </label>
      {(returnPhotos.length > 0 || pickupPhotos.length > 0) && (
        <div>
          <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Attach handover photos as evidence</span>
          <div className="flex gap-2 flex-wrap mt-1.5">
            {[...returnPhotos, ...pickupPhotos].map((p) => (
              <button type="button" key={p.id} onClick={() => toggle(p.id)} className={`relative rounded-lg overflow-hidden border-2 ${value.photo_ids.includes(p.id) ? "border-[#1d7ffd]" : "border-transparent"}`} data-testid={`claim-photo-${p.id}`}>
                <img src={`${API_ORIGIN}${p.url}`} alt="" className="w-14 h-14 object-cover" />
                <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] capitalize">{p.phase}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <UploadRow up={value.uploads} label="Add more photos" testid="claim-extra" />
      <style>{`.ipc{width:100%;background:#F7F8FA;border:1px solid #E2E8F0;border-radius:0.75rem;padding:10px 14px;font-size:14px;color:#0a1f4d;outline:none}.ipc:focus{border-color:#1d7ffd}`}</style>
    </div>
  );
}

export const emptyClaim = (uploads) => ({ amount_claimed: "", description: "", photo_ids: [], uploads });
export const claimPayload = (v) => ({ amount_claimed: Number(v.amount_claimed), description: v.description, photo_ids: v.photo_ids, extra_file_ids: v.uploads.files.map((f) => f.id) });

function OpenClaim({ booking, onChanged }) {
  const uploads = usePhotoUploads();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyClaim(uploads));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/owner/bookings/${booking.id}/claims`, claimPayload({ ...form, uploads }));
      toast.success("Claim opened — the guest has been notified and the deposit is on hold");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open claim");
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <div className="mt-1">
        <p className="text-sm text-[#334155]">Found damage after the return? You can open a claim against the guest's {formatZAR(booking.pricing?.deposit)} deposit within 72 hours of return.</p>
        <button onClick={() => setOpen(true)} className="btn-outline !py-2 text-sm mt-3" data-testid="claim-open-btn"><ShieldAlert size={14} /> Report damage</button>
      </div>
    );
  }
  return (
    <div>
      <ClaimForm booking={booking} value={{ ...form, uploads }} onChange={setForm} />
      <div className="flex gap-2 mt-3">
        <button onClick={() => setOpen(false)} className="btn-outline !py-2 text-sm flex-1" data-testid="claim-cancel">Cancel</button>
        <button onClick={submit} disabled={busy || !form.amount_claimed || form.description.length < 10} className="btn-primary text-sm flex-1 disabled:opacity-60" data-testid="claim-submit">{busy ? "Submitting…" : "Submit claim"}</button>
      </div>
    </div>
  );
}

function GuestResponse({ booking, onChanged }) {
  const uploads = usePhotoUploads();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/claim/respond`, { text, file_ids: uploads.files.map((f) => f.id) });
      toast.success("Response sent — tadada will review both sides");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send response");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 rounded-xl border border-[#1d7ffd]/40 bg-[#1d7ffd]/5 p-3" data-testid="claim-response-form">
      <p className="text-xs font-bold text-[#0a1f4d]">Your response</p>
      <p className="text-xs text-[#64748B] mt-0.5">Explain your side and add photos (e.g. your pickup photos showing the damage was pre-existing). tadada reviews both sides before deciding.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} className="w-full mt-2 bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm text-[#0a1f4d] outline-none focus:border-[#1d7ffd] min-h-[70px]" placeholder="What happened from your side?" data-testid="claim-response-text" />
      <div className="mt-2"><UploadRow up={uploads} label="Add photos" testid="claim-response" /></div>
      <button onClick={submit} disabled={busy || text.trim().length < 5} className="btn-primary text-xs !py-2 mt-3 w-full disabled:opacity-60" data-testid="claim-response-submit">{busy ? "Sending…" : "Send response"}</button>
    </div>
  );
}
